import React, { useEffect, useMemo, useState } from "react";
import {
  Flag, Plus, Edit3, Trash2, CheckCircle, AlertCircle, X, Star, Lock,
  ArrowRight, Calendar, MessageSquare, ChevronDown, ChevronUp, Image as ImageIcon,
  Upload,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { usePortalLang } from "../../lib/PortalLanguageContext";
import { normalizeDateInput } from "./FinanceLedgerSection";
import MilestoneImportDialog from "./MilestoneImportDialog";

const PRESETS = [
  "Site mobilization", "Demolition completion", "Civil works completion",
  "MEP first fix completion", "Ceiling / gypsum framing completion",
  "Ceiling closure", "Marble / flooring start", "Marble / flooring completion",
  "Joinery measurement", "Joinery production start", "Joinery installation start",
  "Kitchen installation", "Lighting / automation installation",
  "Curtains / soft furnishing", "Final painting / finishing", "Snagging",
  "Deep cleaning", "Handover",
];

const RESPONSIBLE_GROUPS = [
  { v: "project_manager", l: "Project Manager" },
  { v: "site_engineer", l: "Site Engineer" },
  { v: "admin_staff", l: "Admin Staff" },
  { v: "client", l: "Client" },
  { v: "client_project_manager", l: "Client PM" },
  { v: "client_interior_designer", l: "Client Designer" },
  { v: "client_associate", l: "Client Associate" },
  { v: "client_subcontractor", l: "Client Subcontractor" },
  { v: "client_supplier", l: "Client Supplier" },
  { v: "subcontractor", l: "Subcontractor" },
  { v: "supplier", l: "Supplier" },
  { v: "delivery_team", l: "Delivery Team" },
  { v: "housekeeping_team", l: "Housekeeping" },
];

const STATUSES = [
  { v: "planned", l: "Planned", cls: "bg-[#7A6B5F] text-white" },
  { v: "in_progress", l: "In Progress", cls: "bg-[#6E1F2C] text-white" },
  { v: "completed", l: "Completed", cls: "bg-[#3F6E2C] text-white" },
  { v: "delayed", l: "Delayed", cls: "bg-[#A3141A] text-white" },
  { v: "blocked", l: "Blocked", cls: "bg-[#1C110C] text-white" },
];

const VISIBILITIES = [
  { v: "client", l: "Client-visible" },
  { v: "internal", l: "Internal only" },
  { v: "public", l: "Public (everyone)" },
];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const t = new Date(dateStr.slice(0, 10));
    return Math.round((t - new Date()) / 86400000);
  } catch { return null; }
}

function MilestoneCard({ slug, m, canManage, isAdmin, onEdit, onDelete, onSetNext }) {
  const { t } = usePortalLang();
  const STATUSES_LOCAL = [
    { v: "planned", l: t("mile.statusPlanned"), cls: "bg-[#7A6B5F] text-white" },
    { v: "in_progress", l: t("mile.statusInProgress"), cls: "bg-[#6E1F2C] text-white" },
    { v: "completed", l: t("mile.statusCompleted"), cls: "bg-[#3F6E2C] text-white" },
    { v: "delayed", l: t("mile.statusDelayed"), cls: "bg-[#A3141A] text-white" },
    { v: "blocked", l: t("mile.statusBlocked"), cls: "bg-[#1C110C] text-white" },
  ];
  const d = daysUntil(m.target_date);
  const overdue = d !== null && d < 0 && m.status !== "completed";
  const st = STATUSES_LOCAL.find((s) => s.v === m.status) || STATUSES_LOCAL[0];
  const respLabel = RESPONSIBLE_GROUPS.find((g) => g.v === m.responsible_group)?.l;
  const depLabel = RESPONSIBLE_GROUPS.find((g) => g.v === m.dependency_group)?.l;
  const pct = Math.max(0, Math.min(100, m.progress_percent || 0));
  const [showUpdates, setShowUpdates] = useState(false);
  const [updatesData, setUpdatesData] = useState(null);
  const [loadingUpdates, setLoadingUpdates] = useState(false);

  const loadUpdates = async () => {
    if (updatesData) {
      setShowUpdates((v) => !v);
      return;
    }
    setLoadingUpdates(true);
    try {
      const data = await portalApi.getMilestoneUpdates(slug, m.id);
      setUpdatesData(data);
      setShowUpdates(true);
    } catch {
      setUpdatesData({ updates: [], count: 0 });
      setShowUpdates(true);
    } finally {
      setLoadingUpdates(false);
    }
  };
  return (
    <article
      data-testid={`portal-milestone-card-${m.id}`}
      className="relative bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/40 hover:shadow-[0_10px_30px_-15px_rgba(110,31,44,0.25)] transition-all overflow-hidden"
    >
      {m.is_next_milestone && (
        <div
          data-testid={`portal-milestone-next-${m.id}`}
          className="absolute top-0 left-0 right-0 bg-[#D4A963] text-[#1C110C] text-[9px] tracking-[0.3em] uppercase font-medium text-center py-1"
        >
          ★ Next milestone for this week
        </div>
      )}
      <div className={`p-5 ${m.is_next_milestone ? "pt-9" : ""}`}>
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${st.cls}`}>{st.l}</span>
            {overdue && (
              <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#A3141A] text-white inline-flex items-center gap-1">
                <AlertCircle size={9} /> {Math.abs(d)} d overdue
              </span>
            )}
            {m.criticality && (
              <span className="text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 border border-[#A3141A]/40 text-[#A3141A]">
                {m.criticality}
              </span>
            )}
          </div>
          {canManage && (
            <div className="flex items-center gap-1">
              {!m.is_next_milestone && (
                <button
                  type="button"
                  onClick={() => onSetNext(m)}
                  data-testid={`portal-milestone-set-next-${m.id}`}
                  className="p-1.5 border border-[#D4A963] text-[#A6822F] hover:bg-[#D4A963] hover:text-[#1C110C]"
                  title="Set as next milestone for this week"
                >
                  <Star size={12} />
                </button>
              )}
              <button
                type="button"
                onClick={() => onEdit(m)}
                data-testid={`portal-milestone-edit-${m.id}`}
                className="p-1.5 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC]"
              >
                <Edit3 size={12} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(m)}
                data-testid={`portal-milestone-delete-${m.id}`}
                className="p-1.5 border border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-white"
              >
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </div>
        <h3 className="font-serif-display text-lg text-[#1C110C] leading-tight">{m.name}</h3>
        {m.target_date && (
          <p className="text-[11px] text-[#7A6B5F] mt-1 inline-flex items-center gap-1">
            <Calendar size={10} /> Target {m.target_date}
            {d !== null && !overdue && m.status !== "completed" && ` · ${d} d left`}
          </p>
        )}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] tracking-[0.26em] uppercase text-[#3E2A22]">Progress</span>
            <span className="text-[11px] text-[#6E1F2C] font-medium">{pct}%</span>
          </div>
          <div className="h-1.5 bg-[#FAF5EC] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${m.status === "completed" ? "bg-[#3F6E2C]" : "bg-gradient-to-r from-[#D4A963] via-[#A6822F] to-[#6E1F2C]"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-[#6E1F2C]/10 grid grid-cols-2 gap-3 text-[10px]">
          {respLabel && (
            <div>
              <p className="tracking-[0.24em] uppercase text-[#7A6B5F]">Responsible</p>
              <p className="text-[#1C110C] mt-0.5">{respLabel}</p>
            </div>
          )}
          {depLabel && (
            <div>
              <p className="tracking-[0.24em] uppercase text-[#7A6B5F]">Dependency</p>
              <p className="text-[#1C110C] mt-0.5">{depLabel}</p>
            </div>
          )}
          {m.dependency_notes && (
            <p className="col-span-2 text-[11px] text-[#3E2A22] italic border-l-2 border-[#D4A963] pl-2">{m.dependency_notes}</p>
          )}
          {isAdmin && m.financial_value != null && (
            <div className="col-span-2 inline-flex items-center gap-2 bg-[#FAF5EC] border border-[#D4A963]/40 px-2.5 py-1.5">
              <Lock size={10} className="text-[#A6822F]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">Admin · value</span>
              <span className="text-xs text-[#1C110C] font-medium ml-auto">{Number(m.financial_value).toLocaleString()} AED</span>
            </div>
          )}
        </div>

        {/* Slice C — Linked daily updates */}
        <div className="mt-4 pt-3 border-t border-[#6E1F2C]/10">
          <button
            type="button"
            onClick={loadUpdates}
            data-testid={`portal-milestone-toggle-updates-${m.id}`}
            className="w-full flex items-center justify-between gap-2 text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] hover:text-[#56151F]"
          >
            <span className="inline-flex items-center gap-1.5">
              <MessageSquare size={11} />
              {loadingUpdates ? t("msg.loading") : showUpdates ? t("mile.hideUpdates") : t("mile.showUpdates")}
              {updatesData?.count != null && ` (${updatesData.count})`}
            </span>
            {showUpdates ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {showUpdates && updatesData && (
            <div
              data-testid={`portal-milestone-updates-panel-${m.id}`}
              className="mt-3 space-y-2"
            >
              {updatesData.updates.length === 0 && (
                <p className="text-[11px] text-[#7A6B5F] italic">
                  {t("mile.noUpdatesLinked")}
                </p>
              )}
              {updatesData.updates.slice(0, 5).map((u) => (
                <div key={u.id} className="bg-[#FAF5EC]/60 border-l-2 border-[#D4A963] pl-2.5 py-1.5">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
                    <Calendar size={9} /> {u.date}
                    {u.author?.name && <span className="text-[#7A6B5F] normal-case tracking-normal">· {u.author.name}</span>}
                  </p>
                  <p className="text-xs text-[#1C110C] mt-0.5 line-clamp-2 leading-snug">{u.work_completed || "—"}</p>
                  {u.photo_urls?.length > 0 && (
                    <div className="mt-1.5 flex gap-1">
                      {u.photo_urls.slice(0, 4).map((url, i) => (
                        <img
                          key={u.photo_ids[i]}
                          src={url}
                          alt={u.photo_captions?.[i] || ""}
                          loading="lazy"
                          className="w-10 h-10 object-cover border border-[#6E1F2C]/15"
                        />
                      ))}
                      {u.photo_urls.length > 4 && (
                        <span className="text-[9px] text-[#7A6B5F] inline-flex items-center pl-1">
                          <ImageIcon size={9} className="mr-1" />+{u.photo_urls.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {updatesData.updates.length > 5 && (
                <p className="text-[10px] text-[#7A6B5F] italic">+ {updatesData.updates.length - 5} more update(s)</p>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function MilestoneFormModal({ slug, milestone, onClose, onSaved, isAdmin }) {
  const isEdit = !!milestone?.id;
  const [form, setForm] = useState({
    name: milestone?.name || "",
    target_date: milestone?.target_date || "",
    start_date: milestone?.start_date || "",
    status: milestone?.status || "planned",
    progress_percent: milestone?.progress_percent || 0,
    visibility: milestone?.visibility || "client",
    responsible_group: milestone?.responsible_group || "project_manager",
    dependency_group: milestone?.dependency_group || "",
    dependency_notes: milestone?.dependency_notes || "",
    notes: milestone?.notes || "",
    is_next_milestone: milestone?.is_next_milestone || false,
    financial_value: milestone?.financial_value || "",
    criticality: milestone?.criticality || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr("Title is required.");
    setBusy(true); setErr("");
    const payload = { ...form };
    // Read dates straight from the DOM (freshest value — some native pickers
    // update the input without firing React onChange), then normalize to ISO.
    const startEl = e.target.querySelector('[data-testid="portal-milestone-start"]');
    const targetEl = e.target.querySelector('[data-testid="portal-milestone-target"]');
    const rawStart = (startEl && startEl.value) || form.start_date || "";
    const rawTarget = (targetEl && targetEl.value) || form.target_date || "";
    payload.start_date = rawStart ? normalizeDateInput(rawStart) : "";
    payload.target_date = rawTarget ? normalizeDateInput(rawTarget) : "";
    if (rawStart && !payload.start_date) { setBusy(false); return setErr("Planned start must be a valid date (YYYY-MM-DD)."); }
    if (rawTarget && !payload.target_date) { setBusy(false); return setErr("Target date must be a valid date (YYYY-MM-DD)."); }
    if (payload.financial_value === "") delete payload.financial_value;
    else payload.financial_value = Number(payload.financial_value) || 0;
    if (!isAdmin) delete payload.financial_value;
    try {
      if (isEdit) {
        await portalApi.patchMilestone(slug, milestone.id, payload);
      } else {
        await portalApi.createMilestone(slug, payload);
      }
      onSaved && onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        data-testid="portal-milestone-form"
        className="bg-white w-full max-w-[640px] border border-[#6E1F2C]/20 my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <h3 className="font-serif-display text-xl text-[#1C110C]">{isEdit ? "Edit milestone" : "New milestone"}</h3>
          <button type="button" onClick={onClose} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {!isEdit && (
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">Quick preset</p>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => set("name", p)}
                    data-testid={`portal-milestone-preset-${p.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g,'')}`}
                    className={`text-[10px] tracking-wide px-2.5 py-1 border ${
                      form.name === p ? "bg-[#FAF5EC] border-[#6E1F2C]" : "border-[#6E1F2C]/15 hover:border-[#6E1F2C]/40"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Title *</label>
            <input
              value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="portal-milestone-name"
              className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#6E1F2C]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Start date</label>
              <input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="portal-milestone-start" className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Target date</label>
              <input type="date" value={form.target_date || ""} onChange={(e) => set("target_date", e.target.value)} data-testid="portal-milestone-target" className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Progress %</label>
              <input type="number" min="0" max="100" value={form.progress_percent} onChange={(e) => set("progress_percent", Math.max(0, Math.min(100, Number(e.target.value) || 0)))} className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Responsible</label>
              <select value={form.responsible_group} onChange={(e) => set("responsible_group", e.target.value)} className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm">
                <option value="">—</option>
                {RESPONSIBLE_GROUPS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Dependency</label>
              <select value={form.dependency_group} onChange={(e) => set("dependency_group", e.target.value)} className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm">
                <option value="">—</option>
                {RESPONSIBLE_GROUPS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Dependency notes (internal)</label>
            <input value={form.dependency_notes} onChange={(e) => set("dependency_notes", e.target.value)} placeholder="e.g. Marble delivery required before flooring" className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Visibility</label>
              <select value={form.visibility} onChange={(e) => set("visibility", e.target.value)} className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm">
                {VISIBILITIES.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}
              </select>
            </div>
            {isAdmin && (
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#A6822F] mb-1 inline-flex items-center gap-1">
                  <Lock size={10} /> Financial value (AED)
                </label>
                <input type="number" value={form.financial_value} onChange={(e) => set("financial_value", e.target.value)} data-testid="portal-milestone-financial" className="w-full bg-[#FAF5EC] border border-[#D4A963]/40 px-3 py-2 text-sm" />
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_next_milestone} onChange={(e) => set("is_next_milestone", e.target.checked)} className="accent-[#D4A963]" />
            <span className="text-[11px] text-[#1C110C]"><Star size={11} className="inline mr-1 text-[#A6822F]" />Mark as next milestone for this week</span>
          </label>
          {err && <p className="text-[11px] text-[#A3141A]">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 bg-[#FAF5EC]/40">
          <button type="button" onClick={onClose} className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] px-4 py-2">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="portal-milestone-submit"
            className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
          >
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create milestone")}
          </button>
        </div>
      </form>
    </div>
  );
}

export function MilestonesTab({ slug, canManage, isAdmin }) {
  const { t } = usePortalLang();
  const [timeline, setTimeline] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState("summary"); // "summary" | "all"
  const [openId, setOpenId] = useState(null); // id of milestone open in drawer
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const reload = () => setRefreshKey((k) => k + 1);

  const runRepair = async () => {
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const res = await portalApi.repair4601Milestones();
      setRepairResult({
        ok: true,
        message: `Restored ${res?.after_count ?? 0} milestone(s) (M1–M7). Removed ${res?.removed_stale ?? 0} stale record(s).`,
      });
      reload();
    } catch (e) {
      setRepairResult({ ok: false, message: e?.message || "Repair failed. Please try again." });
    } finally {
      setRepairBusy(false);
    }
  };
  useEffect(() => {
    let alive = true;
    portalApi.getTimeline(slug).then((d) => alive && setTimeline(d)).catch(() => alive && setTimeline(null));
    return () => { alive = false; };
  }, [slug, refreshKey]);

  const filtered = useMemo(() => {
    const items = timeline?.milestones || [];
    if (filter === "upcoming") return items.filter((m) => m.status !== "completed" && (!m.target_date || daysUntil(m.target_date) >= 0));
    if (filter === "completed") return items.filter((m) => m.status === "completed");
    if (filter === "delayed") return items.filter((m) => m.status !== "completed" && m.target_date && daysUntil(m.target_date) < 0);
    if (filter === "this_week") return items.filter((m) => m.target_date && daysUntil(m.target_date) >= 0 && daysUntil(m.target_date) <= 7);
    if (filter === "next_week") return items.filter((m) => m.target_date && daysUntil(m.target_date) > 7 && daysUntil(m.target_date) <= 14);
    return items;
  }, [timeline, filter]);

  const convertable = (timeline?.tasks || []).filter((t) =>
    !timeline.milestones?.some((m) => m.linked_task_id === t.id) &&
    ((t.risk || "").toLowerCase() === "high" || (t.criticality || "").toLowerCase().includes("critical"))
  );

  const setNext = async (m) => {
    try { await portalApi.setNextMilestone(slug, m.id); reload(); } catch { /* ignore */ }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete milestone "${m.name}"?`)) return;
    try { await portalApi.deleteMilestone(slug, m.id); reload(); } catch { /* ignore */ }
  };
  const convertTask = async (t) => {
    try { await portalApi.convertTaskToMilestone(slug, t.id); reload(); } catch { /* ignore */ }
  };

  const items = useMemo(() => timeline?.milestones || [], [timeline]);

  // Group milestones for the calm "Summary" mode (the new default).
  // Each group is shown only when it has items; everything else is hidden
  // behind "View all milestones".
  const groups = useMemo(() => {
    const current = items.filter((m) => m.is_next_milestone);
    const dueThisWeek = items.filter(
      (m) => !m.is_next_milestone &&
        m.status !== "completed" &&
        m.target_date &&
        daysUntil(m.target_date) >= 0 &&
        daysUntil(m.target_date) <= 7,
    );
    const delayed = items.filter(
      (m) => m.status !== "completed" &&
        ((m.target_date && daysUntil(m.target_date) < 0) ||
         m.status === "delayed" ||
         m.status === "blocked"),
    );
    return { current, dueThisWeek, delayed };
  }, [items]);

  const openMilestone = items.find((m) => m.id === openId) || null;

  // Apply search inside "View all" mode.
  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.notes || "").toLowerCase().includes(q) ||
      (m.responsible_group || "").toLowerCase().includes(q),
    );
  }, [filtered, search]);

  return (
    <div data-testid="portal-milestones-tab" className="space-y-8 pb-24 lg:pb-0">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">{t("tab.timeline")}</p>
          <h2 className="font-serif-display text-2xl text-[#1C110C]">{t("mile.title")}</h2>
        </div>
        <div className="flex items-center gap-2">
          {viewMode === "all" && (
            <button
              type="button"
              onClick={() => { setViewMode("summary"); setFilter("all"); setSearch(""); }}
              data-testid="portal-milestones-back-summary"
              className="text-[10px] tracking-[0.22em] uppercase px-3 py-2 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
            >
              {t("mile.backToSummary", "Back to summary")}
            </button>
          )}
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                data-testid="portal-milestone-import-btn"
                className="inline-flex items-center gap-2 border border-[#6E1F2C]/30 hover:border-[#6E1F2C] text-[#6E1F2C] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase"
                title="Import milestones from Excel"
              >
                <Upload size={13} /> Import Excel
              </button>
              <button
                type="button"
                onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="portal-milestone-new-btn"
                className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
              >
                <Plus size={13} /> {t("mile.add")}
              </button>
            </>
          )}
        </div>
      </div>

      {isAdmin && slug === "4601-palm-tower" && items.length === 0 && (
        <div
          data-testid="portal-milestone-repair-banner"
          className="bg-white border border-[#A3141A]/30 border-l-4 border-l-[#A3141A] p-5"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <AlertCircle size={20} className="text-[#A3141A] mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h3 className="font-serif-display text-lg text-[#1C110C]">No milestones found</h3>
              <p className="text-xs text-[#7A6B5F] mt-1 leading-relaxed">
                This project's milestone register is empty. Run the recovery to restore the
                canonical <span className="font-medium text-[#3E2A22]">M1–M7</span> programme for
                4601 Palm Tower. This is safe and idempotent — it never deletes daily updates,
                photos, tasks or reports.
              </p>
              {repairResult && (
                <p
                  data-testid="portal-milestone-repair-result"
                  className={`mt-3 text-xs inline-flex items-center gap-1.5 px-3 py-2 border ${
                    repairResult.ok
                      ? "text-[#3F6E2C] border-[#3F6E2C]/30 bg-[#3F6E2C]/5"
                      : "text-[#A3141A] border-[#A3141A]/30 bg-[#A3141A]/5"
                  }`}
                >
                  {repairResult.ok ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
                  {repairResult.message}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={runRepair}
              disabled={repairBusy}
              data-testid="portal-milestone-repair-btn"
              className="inline-flex items-center gap-2 bg-[#A3141A] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#7f0f14] disabled:opacity-60 flex-shrink-0"
            >
              {repairBusy ? "Repairing…" : "Repair M1–M7 milestones"}
            </button>
          </div>
        </div>
      )}

      {viewMode === "summary" ? (
        <SummaryView
          groups={groups}
          items={items}
          slug={slug}
          isAdmin={isAdmin}
          onOpen={setOpenId}
          onSeeAll={() => setViewMode("all")}
          t={t}
        />
      ) : (
        <FullListView
          items={items}
          filtered={filteredAll}
          slug={slug}
          isAdmin={isAdmin}
          filter={filter}
          setFilter={setFilter}
          search={search}
          setSearch={setSearch}
          onOpen={setOpenId}
          t={t}
        />
      )}

      {canManage && convertable.length > 0 && viewMode === "all" && (
        <section className="mt-8 pt-6 border-t border-[#6E1F2C]/15">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">
            Suggested from Gantt — high-risk / critical activities
          </p>
          <p className="text-[11px] text-[#7A6B5F] mb-3">One-click convert any of these into a milestone:</p>
          <div className="space-y-2">
            {convertable.slice(0, 8).map((task) => (
              <div key={task.id} className="flex items-center justify-between gap-3 bg-white border border-[#6E1F2C]/15 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-[#1C110C] truncate">{task.name}</p>
                  <p className="text-[10px] text-[#7A6B5F]">{task.start_date || "—"} → {task.finish_date || "—"} · risk: {task.risk || "—"}</p>
                </div>
                <button
                  type="button"
                  onClick={() => convertTask(task)}
                  data-testid={`portal-milestone-convert-${task.id}`}
                  className="text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] hover:text-[#56151F] inline-flex items-center gap-1 border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-1.5"
                >
                  Convert <ArrowRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {showForm && (
        <MilestoneFormModal
          slug={slug}
          milestone={editing}
          isAdmin={isAdmin}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); reload(); }}
        />
      )}

      {openMilestone && (
        <MilestoneDetailDrawer
          slug={slug}
          m={openMilestone}
          canManage={canManage}
          isAdmin={isAdmin}
          onClose={() => setOpenId(null)}
          onEdit={(m) => { setOpenId(null); setEditing(m); setShowForm(true); }}
          onDelete={(m) => { setOpenId(null); remove(m); }}
          onSetNext={(m) => { setNext(m); }}
          t={t}
        />
      )}

      {importOpen && (
        <MilestoneImportDialog
          slug={slug}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

/* ===========================================================
 * SLIM CARD — compact, click-to-open. No inline expansion.
 * =========================================================== */
function SlimMilestoneCard({ m, onOpen, t, accent = "default" }) {
  const d = daysUntil(m.target_date);
  const overdue = d !== null && d < 0 && m.status !== "completed";
  const pct = Math.max(0, Math.min(100, m.progress_percent || 0));
  const statusLabel = {
    planned: t("mile.statusPlanned"),
    in_progress: t("mile.statusInProgress"),
    completed: t("mile.statusCompleted"),
    delayed: t("mile.statusDelayed"),
    blocked: t("mile.statusBlocked"),
  }[m.status || "planned"];
  const statusColor = {
    planned: "bg-[#7A6B5F]/12 text-[#3E2A22]",
    in_progress: "bg-[#6E1F2C] text-[#FAF5EC]",
    completed: "bg-[#3F6E2C] text-[#FAF5EC]",
    delayed: "bg-[#A3141A] text-[#FAF5EC]",
    blocked: "bg-[#1C110C] text-[#FAF5EC]",
  }[m.status || "planned"];
  const respLabel = RESPONSIBLE_GROUPS.find((g) => g.v === m.responsible_group)?.l;
  const depLabel = RESPONSIBLE_GROUPS.find((g) => g.v === m.dependency_group)?.l;
  const photoCount = m._update_count ?? null;
  return (
    <button
      type="button"
      onClick={() => onOpen(m.id)}
      data-testid={`portal-milestone-slim-${m.id}`}
      className={`group relative w-full text-left bg-white border ${
        accent === "gold" ? "border-[#D4A963]/55" :
        accent === "red"  ? "border-[#A3141A]/40" :
        "border-[#6E1F2C]/12"
      } hover:border-[#6E1F2C]/50 hover:shadow-[0_10px_30px_-15px_rgba(110,31,44,0.18)] transition-all overflow-hidden`}
    >
      {m.is_next_milestone && (
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4A963] via-[#A6822F] to-[#6E1F2C]" />
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <p className="font-serif-display text-base text-[#1C110C] leading-snug truncate">{m.name}</p>
            <p className="text-[10px] text-[#7A6B5F] mt-1">
              {m.target_date ? new Date(m.target_date).toLocaleDateString() : "—"}
              {d !== null && (
                <span className={`ms-2 ${overdue ? "text-[#A3141A]" : d <= 7 ? "text-[#6E1F2C]" : "text-[#7A6B5F]"}`}>
                  · {overdue ? t("proj.card.daysOverdue", null, { n: Math.abs(d) }) : t("proj.card.daysLeft", null, { n: d })}
                </span>
              )}
            </p>
          </div>
          <span className={`flex-shrink-0 text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${statusColor}`}>
            {statusLabel}
          </span>
        </div>
        <div className="h-[3px] bg-[#FAF5EC] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#D4A963] to-[#6E1F2C] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2.5 flex items-center justify-between text-[10px] text-[#7A6B5F] tracking-wide">
          <span className="truncate">
            {respLabel && <span title={t("mile.responsible", "Responsible")}>● {respLabel}</span>}
            {depLabel && <span className="ms-2" title={t("mile.dependency", "Dependency")}>↳ {depLabel}</span>}
          </span>
          <span className="inline-flex items-center gap-2 text-[#6E1F2C] group-hover:text-[#56151F]">
            {photoCount != null && photoCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <MessageSquare size={9} /> {photoCount}
              </span>
            )}
            <span className="font-medium">{t("mile.openDetails", "Open")} →</span>
          </span>
        </div>
      </div>
    </button>
  );
}

/* ===========================================================
 * SUMMARY VIEW — the new calm default.
 * Shows Current / Next, Due This Week, Delayed/Blocked only.
 * =========================================================== */
function SummaryView({ groups, items, slug, isAdmin, onOpen, onSeeAll, t }) {
  const sections = [
    {
      key: "current",
      title: t("mile.sec.currentNext", "Current & Next milestone"),
      empty: t("mile.sec.currentEmpty", "No milestone is flagged as current. Set one to highlight this week's focus."),
      items: groups.current,
      accent: "gold",
    },
    {
      key: "thisWeek",
      title: t("mile.sec.thisWeek", "Due this week"),
      empty: t("mile.sec.thisWeekEmpty", "No milestones due this week."),
      items: groups.dueThisWeek,
      accent: "default",
    },
    {
      key: "delayed",
      title: t("mile.sec.delayed", "Delayed or high-risk"),
      empty: t("mile.sec.delayedEmpty", "No delayed or blocked milestones — everything is on track."),
      items: groups.delayed,
      accent: "red",
    },
  ];
  return (
    <div className="space-y-10">
      {sections.map((sec) => (
        <section key={sec.key} data-testid={`portal-milestones-section-${sec.key}`}>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-[10px] tracking-[0.36em] uppercase text-[#6E1F2C] font-medium">
              {sec.title}
              {sec.items.length > 0 && (
                <span className="ms-2 text-[#7A6B5F] tracking-[0.2em]">({sec.items.length})</span>
              )}
            </h3>
          </div>
          {sec.items.length === 0 ? (
            <p className="text-xs text-[#7A6B5F] italic border-l-2 border-[#D4A963]/40 ps-3 py-1">
              {sec.empty}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sec.items.map((m) => (
                <SlimMilestoneCard key={m.id} m={m} onOpen={onOpen} t={t} accent={sec.accent} />
              ))}
            </div>
          )}
        </section>
      ))}
      <div className="pt-2 border-t border-[#6E1F2C]/10">
        <button
          type="button"
          onClick={onSeeAll}
          data-testid="portal-milestones-view-all"
          className="text-[11px] tracking-[0.28em] uppercase text-[#6E1F2C] hover:text-[#56151F] inline-flex items-center gap-2 border border-[#6E1F2C]/25 hover:border-[#6E1F2C] px-4 py-2.5"
        >
          {t("mile.viewAll", "View all milestones")} ({items.length}) →
        </button>
      </div>
    </div>
  );
}

/* ===========================================================
 * FULL LIST VIEW — opt-in via "View all"
 * Adds search + retains the existing filter chip behavior.
 * =========================================================== */
function FullListView({ items, filtered, slug, isAdmin, filter, setFilter, search, setSearch, onOpen, t }) {
  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#6E1F2C]/12 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("mile.search", "Search milestones…")}
            data-testid="portal-milestones-search"
            className="flex-1 min-w-[180px] bg-[#FAF5EC] border border-[#6E1F2C]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#6E1F2C]"
          />
          <div className="flex flex-wrap gap-1.5" data-testid="portal-milestones-filter">
            {[
              { v: "all", l: `${t("nav.all", "All")} (${items.length})` },
              { v: "upcoming", l: t("mile.filter.upcoming", "Upcoming") },
              { v: "this_week", l: t("mile.filter.thisWeek", "This week") },
              { v: "next_week", l: t("mile.filter.nextWeek", "Next week") },
              { v: "delayed", l: t("status.delayed") },
              { v: "completed", l: t("status.completed") },
            ].map((f) => (
              <button
                key={f.v}
                type="button"
                onClick={() => setFilter(f.v)}
                data-testid={`portal-milestones-filter-${f.v}`}
                className={`text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border transition-colors ${
                  filter === f.v
                    ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]"
                    : "bg-white text-[#6E1F2C] border-[#6E1F2C]/20 hover:border-[#6E1F2C]"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#6E1F2C]/25 p-12 text-center">
          <Flag className="mx-auto text-[#D4A963] mb-3" size={36} />
          <p className="text-sm text-[#3E2A22]">{t("mile.empty")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((m) => (
            <SlimMilestoneCard key={m.id} m={m} onOpen={onOpen} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ===========================================================
 * MILESTONE DETAIL DRAWER
 * All heavy details (notes, linked Gantt, linked updates+photos,
 * comments placeholder, financial value for admin, edit/delete)
 * live here so the summary view stays calm.
 * =========================================================== */
function MilestoneDetailDrawer({ slug, m, canManage, isAdmin, onClose, onEdit, onDelete, onSetNext, t }) {
  const [updatesData, setUpdatesData] = useState(null);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoadingUpdates(true);
    portalApi.getMilestoneUpdates(slug, m.id)
      .then((data) => alive && setUpdatesData(data))
      .catch(() => alive && setUpdatesData({ updates: [], count: 0 }))
      .finally(() => alive && setLoadingUpdates(false));
    return () => { alive = false; };
  }, [slug, m.id]);

  const statusLabel = {
    planned: t("mile.statusPlanned"),
    in_progress: t("mile.statusInProgress"),
    completed: t("mile.statusCompleted"),
    delayed: t("mile.statusDelayed"),
    blocked: t("mile.statusBlocked"),
  }[m.status || "planned"];
  const pct = Math.max(0, Math.min(100, m.progress_percent || 0));
  const d = daysUntil(m.target_date);
  const respLabel = RESPONSIBLE_GROUPS.find((g) => g.v === m.responsible_group)?.l;
  const depLabel = RESPONSIBLE_GROUPS.find((g) => g.v === m.dependency_group)?.l;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#1C110C]/55 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="portal-milestone-drawer-overlay"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        data-testid="portal-milestone-drawer"
        className="w-full max-w-[560px] bg-[#FAF5EC] h-full overflow-y-auto border-l border-[#6E1F2C]/20"
      >
        {/* Drawer header */}
        <header className="sticky top-0 bg-white border-b border-[#6E1F2C]/12 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F]">{t("mile.title")}</p>
            <h3 className="font-serif-display text-xl text-[#1C110C] leading-snug">{m.name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="portal-milestone-drawer-close"
            className="text-[#7A6B5F] hover:text-[#1C110C] p-1"
            aria-label={t("btn.close")}
          >
            <X size={20} />
          </button>
        </header>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Status pill + progress */}
          <div className="grid grid-cols-[auto,1fr] gap-3 items-center">
            <span className={`text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 ${
              m.status === "completed" ? "bg-[#3F6E2C] text-[#FAF5EC]" :
              m.status === "delayed" ? "bg-[#A3141A] text-[#FAF5EC]" :
              m.status === "blocked" ? "bg-[#1C110C] text-[#FAF5EC]" :
              m.status === "in_progress" ? "bg-[#6E1F2C] text-[#FAF5EC]" :
              "bg-[#7A6B5F]/15 text-[#3E2A22]"
            }`}>
              {statusLabel}
            </span>
            <div>
              <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
                <span>{t("common.progress")}</span>
                <span>{pct}%</span>
              </div>
              <div className="mt-1 h-1.5 bg-white rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#D4A963] to-[#6E1F2C]" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          {/* Key facts */}
          <dl className="grid grid-cols-2 gap-x-5 gap-y-3 text-xs">
            <div>
              <dt className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">{t("common.dueDate")}</dt>
              <dd className="text-[#1C110C] mt-0.5">{m.target_date || "—"}
                {d !== null && (
                  <span className={`ms-2 text-[10px] ${d < 0 ? "text-[#A3141A]" : "text-[#7A6B5F]"}`}>
                    · {d < 0 ? t("proj.card.daysOverdue", null, { n: Math.abs(d) }) : t("proj.card.daysLeft", null, { n: d })}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">{t("mile.responsible", "Responsible")}</dt>
              <dd className="text-[#1C110C] mt-0.5">{respLabel || "—"}</dd>
            </div>
            <div>
              <dt className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">{t("mile.dependency", "Dependency")}</dt>
              <dd className="text-[#1C110C] mt-0.5">{depLabel || "—"}</dd>
            </div>
            {m.linked_task_id && (
              <div>
                <dt className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">{t("daily.taskLabel")}</dt>
                <dd className="text-[#1C110C] mt-0.5 truncate">{m.linked_task_name || m.linked_task_id}</dd>
              </div>
            )}
          </dl>

          {/* Notes */}
          {m.notes && (
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F] mb-1">{t("form.notes")}</p>
              <p className="text-sm text-[#3E2A22] whitespace-pre-wrap leading-relaxed">{m.notes}</p>
            </div>
          )}

          {/* Admin-only financial value */}
          {isAdmin && m.financial_value != null && (
            <div className="bg-[#FFFFFF] border border-[#D4A963]/40 px-3 py-2.5 flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#A6822F]">
                <Lock size={11} /> {t("fin.adminBadge")} · {t("hero.client")}
              </span>
              <span className="font-medium text-[#1C110C]">{Number(m.financial_value).toLocaleString()} AED</span>
            </div>
          )}

          {/* Linked site updates */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
                {t("mile.linkedUpdates", "Linked site updates")}
                {updatesData?.count != null && ` (${updatesData.count})`}
              </p>
            </div>
            {loadingUpdates ? (
              <p className="text-xs text-[#7A6B5F] italic">{t("msg.loading")}</p>
            ) : updatesData?.updates?.length ? (
              <ul className="space-y-2.5">
                {updatesData.updates.slice(0, 8).map((u) => (
                  <li key={u.id} className="bg-white border-l-2 border-[#D4A963] ps-3 py-2 pe-3">
                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#6E1F2C]">
                      <Calendar size={9} className="inline-block me-1" /> {u.date}
                      {u.author?.name && <span className="text-[#7A6B5F] normal-case tracking-normal"> · {u.author.name}</span>}
                    </p>
                    <p className="text-xs text-[#1C110C] mt-0.5 leading-snug">{u.work_completed || "—"}</p>
                    {u.photo_urls?.length > 0 && (
                      <div className="mt-1.5 flex gap-1 flex-wrap">
                        {u.photo_urls.slice(0, 5).map((url, i) => (
                          <img
                            key={u.photo_ids[i]}
                            src={url}
                            alt={u.photo_captions?.[i] || ""}
                            loading="lazy"
                            className="w-12 h-12 object-cover border border-[#6E1F2C]/15"
                          />
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-[#7A6B5F] italic">{t("mile.noUpdatesLinked")}</p>
            )}
          </section>

          {/* Admin/PM controls */}
          {canManage && (
            <footer className="pt-5 mt-2 border-t border-[#6E1F2C]/12 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {!m.is_next_milestone && (
                  <button
                    type="button"
                    onClick={() => onSetNext(m)}
                    data-testid="portal-milestone-drawer-set-next"
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase border border-[#D4A963] text-[#A6822F] hover:bg-[#D4A963] hover:text-[#1C110C] px-3 py-1.5"
                  >
                    <Star size={11} /> {t("mile.setNext")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onEdit(m)}
                  data-testid="portal-milestone-drawer-edit"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase border border-[#6E1F2C]/30 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] px-3 py-1.5"
                >
                  <Edit3 size={11} /> {t("btn.edit")}
                </button>
              </div>
              <button
                type="button"
                onClick={() => onDelete(m)}
                data-testid="portal-milestone-drawer-delete"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase border border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-[#FAF5EC] px-3 py-1.5"
              >
                <Trash2 size={11} /> {t("btn.delete")}
              </button>
            </footer>
          )}
        </div>
      </aside>
    </div>
  );
}
